/**
 * PRP-MCP-15 hardening (release gate 2026-10-28): a heading-data problem must never blank a
 * page. The headings UI reads every /me/headings response through these, so a missing field
 * becomes its empty default and a response with no usable shape becomes a thrown error that
 * the caller's existing catch turns into a small inline message. Kept apart from headingsApi.ts
 * so component tests that mock the API still run the real checks.
 */
import type { EstimateHeading, Heading } from './headingsApi';
import type { HeadingStyle } from '../types/headingStyle';

const DEFAULT_LIMIT = 25;

/** GET /me/headings. Throws when there is no headings array. Drops entries without an id. */
export function headingList(d: unknown): { headings: Heading[]; limit: number } {
  const o = d as { headings?: unknown; limit?: unknown } | null | undefined;
  if (!o || !Array.isArray(o.headings)) throw new Error('Unexpected headings response');
  const headings = (o.headings as Heading[]).filter((h) => h && typeof h.id === 'string');
  return { headings, limit: typeof o.limit === 'number' ? o.limit : DEFAULT_LIMIT };
}

/** GET/PUT /me/headings/estimate/{hash}. Throws when there is no `state`; fills the rest. */
export function estimateHeading(d: unknown): EstimateHeading {
  const o = d as Partial<EstimateHeading> | null | undefined;
  if (!o || typeof o.state !== 'string') throw new Error('Unexpected estimate heading response');
  return {
    heading_id: o.heading_id ?? null,
    state: o.state,
    heading: o.heading ?? null,
    overrides: {
      opening_statement: o.overrides?.opening_statement ?? null,
      closing_statement: o.overrides?.closing_statement ?? null,
      estimator: o.overrides?.estimator ?? null,
    },
    effective: o.effective ?? null,
  };
}

/** A stored style with missing keys gets the built-in value for each (never "undefined" text). */
export function fullStyle(value: Partial<HeadingStyle> | null | undefined, base: HeadingStyle): HeadingStyle {
  const v = value ?? {};
  return {
    bold: typeof v.bold === 'boolean' ? v.bold : base.bold,
    underline: typeof v.underline === 'boolean' ? v.underline : base.underline,
    case: v.case === 'as_typed' || v.case === 'title' || v.case === 'upper' ? v.case : base.case,
    prefix: typeof v.prefix === 'string' ? v.prefix : base.prefix,
    suffix: typeof v.suffix === 'string' ? v.suffix : base.suffix,
  };
}
